import { useEffect, useRef, useState } from 'react';

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  window.matchMedia &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

// Reveal-on-scroll hook. Content is ALWAYS made visible – the observer only
// controls the subtle entrance animation. A guaranteed fallback timer ensures
// nothing can ever stay hidden (e.g. sections taller than the viewport, no IO,
// reduced motion), so no empty areas appear under headings.
export const useReveal = (options = { threshold: 0.12 }) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (prefersReducedMotion() || typeof IntersectionObserver === 'undefined') {
      setVisible(true);
      return;
    }
    const el = ref.current;
    if (!el) {
      setVisible(true);
      return;
    }
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          obs.disconnect();
        }
      },
      { threshold: 0, rootMargin: '0px 0px -40px 0px', ...options, }
    );
    obs.observe(el);
    // Guaranteed fallback: reveal after a short delay no matter what.
    const fallback = setTimeout(() => setVisible(true), 700);
    return () => {
      obs.disconnect();
      clearTimeout(fallback);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { ref, visible };
};

export const Reveal = ({ children, className = '', delay = 0 }) => {
  const { ref, visible } = useReveal();
  return (
    <div
      ref={ref}
      className={className}
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(16px)',
        transition: `opacity 0.6s ease ${delay}ms, transform 0.6s cubic-bezier(0.22,1,0.36,1) ${delay}ms`,
        willChange: 'opacity, transform',
      }}
    >
      {children}
    </div>
  );
};
